import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

// ---------------------------------------------------------------------------
// KTX2 / PBR Texture Loading System
// ---------------------------------------------------------------------------
// Phase 2 graphics upgrade: replaces procedural canvas textures with real
// PBR texture sets (color, normal, ORM) compressed as KTX2 via Basis Universal.
//
// Texture directory layout:
//   /textures/{material-name}/color.ktx2
//   /textures/{material-name}/normal.ktx2
//   /textures/{material-name}/orm.ktx2
//
// ORM packing: AO (R channel), Roughness (G channel), Metalness (B channel)
// ---------------------------------------------------------------------------

/** A loaded set of PBR textures for a single material. */
export interface PBRTextureSet {
  color: THREE.Texture | null;
  normal: THREE.Texture | null;
  orm: THREE.Texture | null;
}

/** Definition of a texture set in the registry. */
export interface TextureSetDefinition {
  /** Base path to the texture directory, e.g. '/textures/Bricks038/' */
  path: string;
  /** Whether this set includes a color (albedo) map */
  hasColor: boolean;
  /** Whether this set includes a normal map */
  hasNormal: boolean;
  /** Whether this set includes an ORM (AO/Roughness/Metalness) map */
  hasORM: boolean;
  /** Default UV tiling [x, y]. Defaults to [1, 1] if omitted. */
  repeat?: [number, number];
  /** Normal map strength multiplier. Defaults to 1.0 if omitted. */
  normalScale?: number;
  /** AO map intensity. Defaults to 1.0 if omitted. */
  aoIntensity?: number;
}

/**
 * Registry mapping MatKey material names to their KTX2 texture set definitions.
 *
 * Material names match the MatKey type from scene-materials.ts plus additional
 * sets for future use.
 */
export const TEXTURE_SET_REGISTRY: Readonly<Record<string, TextureSetDefinition>> = {
  // -- Core map materials (keys match matKeyToTextureSet values) --

  'brick-desert': {
    path: '/textures/brick-desert/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.2,
    aoIntensity: 1.0,
  },
  'brick-dark': {
    path: '/textures/brick-dark/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.0,
    aoIntensity: 0.8,
  },
  'concrete-clean': {
    path: '/textures/concrete-clean/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 0.8,
    aoIntensity: 1.0,
  },
  'concrete-weathered': {
    path: '/textures/concrete-weathered/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [3, 3],
    normalScale: 1.0,
    aoIntensity: 1.0,
  },
  'concrete-stained': {
    path: '/textures/concrete-stained/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 0.6,
    aoIntensity: 0.9,
  },
  'sand-dirt': {
    path: '/textures/sand-dirt/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [4, 4],
    normalScale: 0.5,
    aoIntensity: 1.0,
  },
  'wood-planks': {
    path: '/textures/wood-planks/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.0,
    aoIntensity: 1.0,
  },
  'wood-crates': {
    path: '/textures/wood-crates/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.0,
    aoIntensity: 0.8,
  },
  'metal-clean': {
    path: '/textures/metal-clean/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.0,
    aoIntensity: 0.7,
  },
  'metal-rusty': {
    path: '/textures/metal-rusty/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.5,
    aoIntensity: 1.0,
  },

  // -- Additional sets for future use --

  'tiles': {
    path: '/textures/tiles/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [3, 3],
    normalScale: 0.8,
    aoIntensity: 1.0,
  },
  'asphalt': {
    path: '/textures/asphalt/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [4, 4],
    normalScale: 0.4,
    aoIntensity: 0.9,
  },
  'plaster': {
    path: '/textures/plaster/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 0.3,
    aoIntensity: 1.0,
  },
  'corrugated-metal': {
    path: '/textures/corrugated-metal/',
    hasColor: true,
    hasNormal: true,
    hasORM: true,
    repeat: [2, 2],
    normalScale: 1.8,
    aoIntensity: 0.8,
  },
} as const;

/**
 * Loads KTX2-compressed PBR texture sets and caches them for reuse.
 *
 * Usage:
 * ```ts
 * const loader = new PBRTextureLoader(renderer);
 * const sets = await loader.loadAllSets();
 * const brickSet = sets.get('brick')!;
 * material.map = brickSet.color;
 * material.normalMap = brickSet.normal;
 * // Split ORM channels in shader or apply individually
 * ```
 */
export class PBRTextureLoader {
  private readonly ktx2Loader: KTX2Loader;
  private readonly cache: Map<string, PBRTextureSet> = new Map();
  private readonly maxAnisotropy: number;

  constructor(renderer: THREE.WebGLRenderer) {
    this.maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    this.ktx2Loader = new KTX2Loader();
    this.ktx2Loader.setTranscoderPath('/libs/basis/');
    this.ktx2Loader.detectSupport(renderer);
  }

  /**
   * Load a single KTX2 texture file and configure it for tiling PBR use.
   * Returns null if the path is empty (e.g. trim material has no textures).
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise<THREE.Texture>((resolve, reject) => {
      this.ktx2Loader.load(
        url,
        (texture) => {
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.minFilter = THREE.LinearMipmapLinearFilter;
          texture.generateMipmaps = false; // KTX2 includes built-in mipmaps
          texture.anisotropy = this.maxAnisotropy;
          resolve(texture);
        },
        undefined, // onProgress — not used
        (error) => {
          reject(error);
        },
      );
    });
  }

  /**
   * Load all textures for a registered material name.
   *
   * @param name - Material key from TEXTURE_SET_REGISTRY (e.g. 'brick', 'metal')
   * @returns The loaded PBR texture set. Maps that don't exist for this
   *          material are set to null.
   * @throws If the name is not found in the registry.
   */
  async loadTextureSet(name: string): Promise<PBRTextureSet> {
    // Return cached set if already loaded
    const cached = this.cache.get(name);
    if (cached) return cached;

    const def = TEXTURE_SET_REGISTRY[name];
    if (!def) {
      throw new Error(`PBRTextureLoader: unknown texture set "${name}"`);
    }

    // Materials with no PBR set (e.g. trim) return all-null immediately
    if (!def.hasColor && !def.hasNormal && !def.hasORM) {
      const emptySet: PBRTextureSet = { color: null, normal: null, orm: null };
      this.cache.set(name, emptySet);
      return emptySet;
    }

    // Load all available maps in parallel
    const [color, normal, orm] = await Promise.all([
      def.hasColor ? this.loadTexture(def.path + 'color.ktx2') : Promise.resolve(null),
      def.hasNormal ? this.loadTexture(def.path + 'normal.ktx2') : Promise.resolve(null),
      def.hasORM ? this.loadTexture(def.path + 'orm.ktx2') : Promise.resolve(null),
    ]);

    // Apply registry-defined UV repeat to all loaded textures
    const repeat = def.repeat ?? [1, 1];
    for (const tex of [color, normal, orm]) {
      if (tex) {
        tex.repeat.set(repeat[0], repeat[1]);
      }
    }

    // Normal maps should use linear colorspace (not sRGB)
    if (normal) {
      normal.colorSpace = THREE.LinearSRGBColorSpace;
    }

    // ORM is data, not color — ensure linear
    if (orm) {
      orm.colorSpace = THREE.LinearSRGBColorSpace;
    }

    // Color map should be sRGB
    if (color) {
      color.colorSpace = THREE.SRGBColorSpace;
    }

    const set: PBRTextureSet = { color, normal, orm };
    this.cache.set(name, set);
    return set;
  }

  /**
   * Load all registered texture sets in parallel.
   *
   * Skips sets that have no PBR maps (e.g. trim). Returns a Map of all
   * successfully loaded sets keyed by material name.
   */
  async loadAllSets(): Promise<Map<string, PBRTextureSet>> {
    const names = Object.keys(TEXTURE_SET_REGISTRY);

    const results = await Promise.allSettled(
      names.map(async (name) => {
        const set = await this.loadTextureSet(name);
        return [name, set] as const;
      }),
    );

    const loaded = new Map<string, PBRTextureSet>();
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const [name, set] = result.value;
        loaded.set(name, set);
      } else {
        // Texture set not yet downloaded or load failed — silently skip.
        // The material will keep its procedural fallback texture.
        console.warn('PBRTextureLoader: failed to load texture set:', result.reason);
      }
    }

    return loaded;
  }

  /**
   * Get a previously loaded texture set from cache.
   *
   * @returns The cached PBRTextureSet, or null if not yet loaded.
   */
  getLoadedSet(name: string): PBRTextureSet | null {
    return this.cache.get(name) ?? null;
  }

  /**
   * Dispose all loaded textures and release the KTX2 transcoder.
   * After calling dispose(), this loader instance should not be reused.
   */
  dispose(): void {
    for (const [, set] of this.cache) {
      set.color?.dispose();
      set.normal?.dispose();
      set.orm?.dispose();
    }
    this.cache.clear();
    this.ktx2Loader.dispose();
  }
}
